"use client";

import { motion } from "motion/react";
import { useTranslations } from "next-intl";
import { useCallback, useEffect, useRef, useState } from "react";
import { PourquoiGlobeBg } from "@/components/scene-bg/PourquoiGlobeBg";

type Reason = { title: string; description: string };

export function PourquoiSticky() {
  const t = useTranslations("why");
  const reasons = t.raw("points") as Reason[];

  const itemRefs = useRef<(HTMLLIElement | null)[]>([]);
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        // Pick the entry whose center is closest to the viewport center band.
        const intersecting = entries.filter((e) => e.isIntersecting);
        if (intersecting.length === 0) return;
        const best = intersecting.reduce((acc, cur) =>
          cur.intersectionRatio > acc.intersectionRatio ? cur : acc,
        );
        const idx = itemRefs.current.findIndex((el) => el === best.target);
        if (idx >= 0) setActiveIndex(idx);
      },
      {
        // Active band is roughly the middle 30% of the viewport.
        rootMargin: "-35% 0px -45% 0px",
        threshold: [0, 0.25, 0.5, 0.75, 1],
      },
    );
    itemRefs.current.forEach((el) => {
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [reasons.length]);

  const setItemRef = useCallback((index: number) => {
    return (el: HTMLLIElement | null) => {
      itemRefs.current[index] = el;
    };
  }, []);

  return (
    <section id="pourquoi" className="relative">
      <PourquoiGlobeBg />
      <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <header className="flex flex-col gap-5 py-12 sm:py-20 lg:py-28">
          <span data-blend className="inline-flex items-center gap-2 self-start text-[11px] font-medium uppercase tracking-[0.18em]">
            <span aria-hidden className="size-1 rounded-full bg-aurora-cyan" />
            {t("eyebrow")}
          </span>
          <h2 data-blend className="max-w-3xl font-sans text-4xl font-medium leading-[1.05] tracking-[-0.03em] sm:text-5xl md:text-6xl">
            {t("title").split(" ").slice(0, -2).join(" ")}{" "}
            <span data-blend className="font-display italic">
              {t("title").split(" ").slice(-2).join(" ")}
            </span>
          </h2>
          <p data-blend className="max-w-2xl text-base sm:text-lg">
            {t("subtitle")}
          </p>
        </header>

        <div className="grid gap-8 pb-12 sm:pb-20 lg:grid-cols-12 lg:gap-12 lg:pb-32">
          {/* Sticky narrative column — grands titres serif italiques qui
              s'enchaînent au scroll. Desktop only. */}
          <div className="hidden lg:col-span-5 lg:block">
            <div className="sticky top-28 flex h-[60vh] items-center">
              <div className="relative h-[60vh] w-full">
                {reasons.map((r, i) => (
                  <motion.div
                    key={r.title}
                    initial={false}
                    animate={{
                      opacity: i === activeIndex ? 1 : 0,
                      y: i === activeIndex ? 0 : 12,
                    }}
                    transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                    className="absolute inset-0 flex flex-col justify-center gap-6"
                  >
                    <div className="flex items-baseline gap-4">
                      <span data-blend className="font-mono text-xs uppercase tracking-[0.2em]">
                        / {String(i + 1).padStart(2, "0")}
                      </span>
                      <span className="hairline flex-1" />
                    </div>
                    <p data-blend className="font-display text-[clamp(3rem,7vw,6rem)] leading-[0.95]">
                      {r.title}
                    </p>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>

          {/* Reasons — cards glass dépoli sur la colonne de droite. */}
          <ol className="flex flex-col gap-4 lg:col-span-7 lg:gap-6">
            {reasons.map((r, i) => {
              const isActive = i === activeIndex;
              return (
                <li key={r.title} ref={setItemRef(i)} className="lg:flex lg:min-h-[70vh] lg:flex-col lg:justify-center">
                  <article className="pilier-glass flex flex-col gap-4 p-6 lg:p-7">
                    <div className="flex items-center gap-3">
                      <motion.span
                        aria-hidden
                        animate={{
                          scale: isActive ? 1 : 0.55,
                          opacity: isActive ? 1 : 0.45,
                        }}
                        transition={{
                          duration: 0.4,
                          ease: [0.22, 1, 0.36, 1],
                        }}
                        className="size-1.5 rounded-full bg-aurora-cyan shadow-[0_0_10px_var(--aurora-cyan)]"
                      />
                      <span className="font-mono text-xs uppercase tracking-[0.2em] text-slate-700">
                        / {String(i + 1).padStart(2, "0")}
                      </span>
                    </div>
                    <h3 className="font-sans text-2xl font-semibold tracking-[-0.02em] text-black sm:text-3xl">
                      {r.title}
                    </h3>
                    <p className="max-w-xl text-base leading-relaxed text-black sm:text-lg">
                      {r.description}
                    </p>
                  </article>
                </li>
              );
            })}
          </ol>
        </div>
      </div>
    </section>
  );
}
